import React, { useState } from "react";

/**
 * ActionButtons - Componente para renderizar botones de acción
 * 
 * @param {Array} actions - Array de definiciones de acciones
 * @param {Object} document - Documento sobre el cual se ejecutan las acciones
 */
const ActionButtons = ({ actions = [], document }) => {
    const [confirmAction, setConfirmAction] = useState(null);

    const handleActionClick = (action, e) => {
        e.stopPropagation(); // Evitar que se propague al onClick de la fila

        if (resolveDisabled(action)) return; // no-op si está deshabilitado

        if (action.confirm) {
            setConfirmAction(action);
        } else {
            action.onClick(document);
        }
    };

    // disabled y title pueden ser un valor directo o una función(doc). Los resolvemos aquí
    // para que cada acción pueda decidir por documento (ej. "Aprobar revisión" se deshabilita
    // si faltan metadatos obligatorios en ese doc).
    const resolveDisabled = (action) => {
        if (typeof action.disabled === 'function') return Boolean(action.disabled(document));
        return Boolean(action.disabled);
    };
    const resolveTitle = (action) => {
        if (typeof action.title === 'function') return action.title(document) || action.label;
        return action.title || action.label;
    };

    const handleConfirm = () => {
        if (confirmAction) {
            confirmAction.onClick(document);
            setConfirmAction(null);
        }
    };

    const handleCancel = () => {
        setConfirmAction(null);
    };

    if (actions.length === 0) {
        return null;
    }

    return (
        <>
            <div style={styles.buttonGroup}>
                {actions.map(action => {
                    const isDisabled = resolveDisabled(action);
                    return (
                        <button
                            key={action.key}
                            onClick={(e) => handleActionClick(action, e)}
                            title={resolveTitle(action)}
                            disabled={isDisabled}
                            style={{
                                ...styles.actionButton,
                                ...(isDisabled ? styles.actionButtonDisabled : {})
                            }}
                            className="action-button-workflow"
                        >
                            {action.icon}
                        </button>
                    );
                })}
            </div>

            {/* Modal de confirmación */}
            {confirmAction && (
                <div style={styles.modalOverlay} onClick={handleCancel}>
                    <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <h3 style={styles.modalTitle}>Confirmación</h3>
                        <p style={styles.modalMessage}>
                            {confirmAction.confirmMessage || `¿Está seguro de ${confirmAction.label}?`}
                        </p>
                        <div style={styles.modalActions}>
                            <button
                                onClick={handleCancel}
                                style={styles.cancelButton}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleConfirm}
                                style={{
                                    ...styles.confirmButton,
                                    backgroundColor: confirmAction.color || '#00468B'
                                }}
                            >
                                {confirmAction.label}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

const styles = {
    buttonGroup: {
        display: "flex",
        gap: "8px",
        justifyContent: "center",
        alignItems: "center",
    },
    actionButton: {
        backgroundColor: "#00468B",
        color: "#FFFFFF",
        border: "1px solid #003366",
        borderRadius: "8px",
        padding: "8px 10px",
        cursor: "pointer",
        fontSize: "16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "all 0.2s ease",
        boxShadow: "0 2px 4px rgba(0, 70, 139, 0.2)",
    },
    actionButtonDisabled: {
        backgroundColor: "#B0B8C3",
        borderColor: "#9097A2",
        cursor: "not-allowed",
        opacity: 0.6,
        boxShadow: "none",
    },
    modalOverlay: {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
    },
    modalContent: {
        backgroundColor: "#fff",
        borderRadius: "8px",
        padding: "24px",
        maxWidth: "400px",
        width: "90%",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    },
    modalTitle: {
        margin: "0 0 16px 0",
        fontSize: "20px",
        fontWeight: "bold",
        color: "#333",
    },
    modalMessage: {
        margin: "0 0 24px 0",
        fontSize: "14px",
        color: "#666",
        lineHeight: "1.5",
    },
    modalActions: {
        display: "flex",
        gap: "12px",
        justifyContent: "flex-end",
    },
    cancelButton: {
        padding: "8px 16px",
        backgroundColor: "#f5f5f5",
        color: "#333",
        border: "1px solid #ddd",
        borderRadius: "4px",
        cursor: "pointer",
        fontSize: "14px",
        fontWeight: "bold",
    },
    confirmButton: {
        padding: "8px 16px",
        color: "#fff",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
        fontSize: "14px",
        fontWeight: "bold",
    },
};

export default ActionButtons;
